import { Injectable, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import {
  CartExportService,
  GungModalService,
  SaveCartModalConfigService
} from 'gung-standard';
import { first, forkJoin, of, switchMap } from 'rxjs';
import { CarLinerCreateQuotationModalComponent } from '../components/car-liner-create-quotation-modal/car-liner-create-quotation-modal.component';
import { CarLinerPdfDownloadIndicatorServiceService } from './car-liner-pdf-download-indicator-service.service';
import { CarLinerCartService } from './car-liner-cart.service';
import { CarLinerMysetupService, MySetup } from './car-liner-mysetup.service';

export interface CarLinerQuotationData {
  name: string;
  customerName: string;
  yourReference: string;
  notes: string;
  date: string;
  expireDate: string;
  ourReference: string;
  paymentTerms: string;
  deliveryTerms: string;
  description: string;
  closed?: boolean;
  showIncludedItems?: boolean;
  showImagesForIncludedItems?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CarLinerSaveCartModalConfigService extends SaveCartModalConfigService {

  protected carLinerpdfDownloadIndicatorService = inject(CarLinerPdfDownloadIndicatorServiceService);
  protected carLinerMysetupService = inject(CarLinerMysetupService);
  protected cartExportService = inject(CartExportService);
  protected cartsService = inject(CarLinerCartService);

  version = 'V2.0-20211015';
  enteredData: CarLinerQuotationData = {
    name: null,
    customerName: null,
    yourReference: null,
    notes: null,
    date: null,
    expireDate: null,
    ourReference: null,
    paymentTerms: null,
    deliveryTerms: null,
    description: null
  };


  carLinerOpenCreateQuotationModal(vatValue, totalValueIncludingVat, cartVatValue, isVatSelected: boolean): void {
    this.carLinerMysetupService
      .getMySetup()
      .pipe(first())
      .subscribe(mySetup => {
        this.quotationAssignSetup(mySetup);
        const id = CarLinerCreateQuotationModalComponent.name;
        const ref = this.ngbModal.open(CarLinerCreateQuotationModalComponent, {
          size: 'lg',
          backdrop: 'static',
          keyboard: false
        });
        ref.componentInstance.enteredData = this.enteredData;
        ref.componentInstance.delegate = this;
        ref.result.then(
          result => {
            if (result.closed) {
              result.closed = false;
              this.enteredData = result;
              return;
            }

            const name = result.name;
            const closed = result.closed;
            const conditions = {
              conditions: {
                ...result.extra
              }
            };
            if (name && !closed) {
              const vat = {
                vatValue: vatValue,
                totalValueIncludingVat: totalValueIncludingVat,
                cartVatValue: cartVatValue
              };

              this.cartsService
                .saveQuotationWithConditions(name, result.description, conditions.conditions, vat, isVatSelected)
                .pipe(
                  switchMap(cart =>
                    forkJoin({
                      cart: of(cart),
                      mysetup: this.carLinerMysetupService.getMySetup().pipe(first())
                    })
                  )
                )
                .subscribe(({ cart, mysetup }) => {
                  this.cartsService.updateSavedQuotesSubject();
                  cart.extra.language = mysetup?.extra?.language || '';

                  if (!cart.extra.mysetup.extra.quotations.showIncludedItems) {
                    this.carLinerpdfDownloadIndicatorService.setDownloading({ downloading: true, data: null });
                    this.cartExportService
                      .exportSavedQuoteBytes(cart, name, undefined, undefined, undefined, conditions)
                      .subscribe(x => {
                        this.carLinerpdfDownloadIndicatorService.setDownloading({ downloading: false, data: null });
                      });
                  } else {
                    this.cartExportService
                      .exportSavedQuote(cart, null, conditions, {
                          overrideFilename: result.name + '.pdf',
                          overrideSubject: "Quotation PDF"
                       }, false)
                      .pipe(first())
                      .subscribe();
                  }
                });
            }
          },
          reason => {}
        );
        this.modalRef.set(id, ref);
      });
  }

  quotationAssignSetup(mySetup: MySetup) {
    if (!mySetup) {
      this.enteredData.customerName = this.enteredData.customerName || '';
      this.enteredData.yourReference = this.enteredData.yourReference || '';
      this.enteredData.ourReference = this.enteredData.ourReference || '';
      this.enteredData.notes = this.enteredData.notes || '';
      this.enteredData.paymentTerms = this.enteredData.paymentTerms || '';
      this.enteredData.deliveryTerms = this.enteredData.deliveryTerms || '';
      return;
    }
    if (!this.enteredData.customerName) {
      this.enteredData.customerName = mySetup.name;
    }
    if (!this.enteredData.yourReference) {
      this.enteredData.yourReference = mySetup.extra?.quotations?.yourReference;
    }
    if (!this.enteredData.ourReference) {
      this.enteredData.ourReference = mySetup.extra?.quotations?.ourReference;
    }
    if (!this.enteredData.notes) {
      this.enteredData.notes = mySetup.extra?.quotations?.notes;
    }
    if (!this.enteredData.paymentTerms) {
      this.enteredData.paymentTerms = mySetup.extra?.quotations?.paymentTerms;
    }
    if (!this.enteredData.deliveryTerms) {
      this.enteredData.deliveryTerms = mySetup.extra?.quotations?.deliveryTerms;
    }
  }
}
